/*
 * This class is copied verbatim from Backstage 1.24.0.
 */

import { CompoundEntityRef, DEFAULT_NAMESPACE } from '@mainstage/catalog-model';

import { useRouteRef } from '@mainstage/core-plugin-api';
import { entityRouteRef } from '@mainstage/plugin-catalog-react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import partition from 'lodash/partition';
import { BackButton, ViewComponentButton } from '../Buttons';
import { EntityListComponent } from '../EntityListComponent';
import { PrepareResult } from '../useImportState';
import { WbButton, WbLink } from '@agilelab/plugin-wb-platform';

type Props = {
  prepareResult: PrepareResult;
  onReset: () => void;
  redirectRoute?: string;
  redirectLabel?: string;
  canRegisterAnother?: boolean;
};

// Among the newly registered entities, return a software entity (e.g. Component, API, Resource)
const filterComponentEntity = (
  newLocations: Array<{
    exists?: boolean;
    target: string;
    entities: CompoundEntityRef[];
  }>,
): CompoundEntityRef | null => {
  for (const location of newLocations) {
    for (const entity of location.entities) {
      if (
        ['component', 'api', 'resource'].includes(
          entity.kind.toLocaleLowerCase('en-US'),
        )
      ) {
        return {
          kind: entity.kind.toLocaleLowerCase('en-US'),
          namespace:
            entity.namespace?.toLocaleLowerCase('en-US') ?? DEFAULT_NAMESPACE,
          name: entity.name,
        };
      }
    }
  }

  return null;
};

export const StepFinishImportLocation = ({
  prepareResult,
  onReset,
  redirectRoute,
  redirectLabel,
  canRegisterAnother = true,
}: Props) => {
  const theme = useTheme();

  const furtherActions = (
    <>
      {canRegisterAnother && (
        <BackButton onClick={onReset}>Register another</BackButton>
      )}
      {redirectRoute && (
        <WbButton
          variant="outlined"
          label={redirectLabel || 'Back'}
          style={{ margin: theme.spacing(1, 1, 0, 0) }}
          to={redirectRoute}
        />
      )}
    </>
  );
  const entityRoute = useRouteRef(entityRouteRef);

  if (prepareResult.type === 'repository') {
    return (
      <>
        <Typography paragraph>
          The following Pull Request has been opened:{' '}
          <WbLink to={prepareResult.pullRequest.url}>
            {prepareResult.pullRequest.url}
          </WbLink>
        </Typography>
        <Typography paragraph>
          Your entities will be imported as soon as the Pull Request is merged.
        </Typography>

        <Grid container spacing={0}>
          {furtherActions}
        </Grid>
      </>
    );
  }

  const [existingLocations, newLocations] = partition(
    prepareResult.locations,
    l => l.exists,
  );
  const newComponentEntity = filterComponentEntity(newLocations);
  return (
    <>
      {newLocations.length > 0 && (
        <>
          <Typography>
            The following entities have been added to the catalog:
          </Typography>

          <EntityListComponent
            locations={newLocations}
            locationListItemIcon={() => <LocationOnIcon />}
            withLinks
          />
        </>
      )}
      {existingLocations.length > 0 && (
        <>
          <Typography>
            A refresh was triggered for the following locations:
          </Typography>

          <EntityListComponent
            locations={existingLocations}
            locationListItemIcon={() => <LocationOnIcon />}
            withLinks
          />
        </>
      )}
      <Grid container spacing={0}>
        {newComponentEntity && (
          <ViewComponentButton
            to={`/platform-settings${entityRoute(newComponentEntity)}`}
          >
            View Component
          </ViewComponentButton>
        )}
        {furtherActions}
      </Grid>
    </>
  );
};
